import { message } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { initalPolicies } from "../../const";

export const usePolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [extraBedPrice, setExtraBedPrice] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState(initalPolicies);
  const [childrenPolicy, setChildrenPolicy] = useState({});

  useEffect(() => {
    fetchPoliciesAndExtraBedPrice();
  }, []);

  // Fetch tenant-level children age policy and merge into formData
  useEffect(() => {
    const fetchChildrenPolicy = async () => {
      try {
        const res = await axios.get("/api/childrenpolicy/get");
        if (res && res.status === 200 && res.data) {
          // Normalize and merge into formData; write into both childrenAgeRule and childrenRule for compatibility
          setFormData((prev) => ({
            ...prev,
            // merge childrenAgeRule safely, ensure freeAge and extraPrice present
            childrenAgeRule: {
              freeAge:
                (res.data.childrenAgeRule &&
                  res.data.childrenAgeRule.freeAge) ||
                (prev.childrenAgeRule && prev.childrenAgeRule.freeAge) ||
                0,
              maxAge:
                (res.data.childrenAgeRule && res.data.childrenAgeRule.maxAge) ||
                (prev.childrenAgeRule && prev.childrenAgeRule.maxAge) ||
                0,
              extraPrice:
                res.data.childrenAgeRule &&
                typeof res.data.childrenAgeRule.extraPrice !== "undefined"
                  ? res.data.childrenAgeRule.extraPrice
                  : (prev.childrenAgeRule && prev.childrenAgeRule.extraPrice) ||
                    0,
            },
          }));
          setChildrenPolicy(res.data);
        }
      } catch (err) {
        // ignore 404 (not found) and report others
        if (!(err.response && err.response.status === 404)) {
          console.error("Failed to load tenant children age policy", err);
        }
      }
    };

    fetchChildrenPolicy();
  }, []);

  const fetchPoliciesAndExtraBedPrice = async () => {
    try {
      const [policyResponse, extraBedPriceResponse] = await Promise.all([
        axios.get("/api/policies/getallpolicies"),
        axios.get("/api/extrabedprice/get"),
      ]);
      setPolicies(policyResponse.data);
      setExtraBedPrice(extraBedPriceResponse.data.price);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch policies or extra bed price.");
    }
  };

  const handleCancellationRuleChange = (field, value) => {
    const updatedCancellationRule = {
      ...formData.cancellationRule[0],
      [field]: value,
    };
    setFormData({ ...formData, cancellationRule: [updatedCancellationRule] });
  };

  const handleChildrenRuleChange = (field, value) => {
    setFormData({
      ...formData,
      childrenRule: { ...formData.childrenRule, [field]: value },
    });
  };

  const handleChildrenAgeRuleChange = (field, value) => {
    setFormData({
      ...formData,
      childrenAgeRule: { ...formData.childrenAgeRule, [field]: value },
    });
  };

  const handleModalOk = async () => {
    try {
      if (editingPolicy) {
        await axios.put(`/api/policies/update/${editingPolicy._id}`, formData);
        message.success("Policy updated successfully");
      } else {
        await axios.post("/api/policies/create", formData);
        message.success("Policy created successfully");
      }
      setIsModalVisible(false);
      fetchPoliciesAndExtraBedPrice();
    } catch (error) {
      message.error("Failed to save policy");
    }
  };

  const handleCreate = () => {
    setEditingPolicy(null);
    setFormData({
      cancellationRule: [
        { chargeType: "Flexible", daysBeforeCheckin: 0, chargePercentage: 0 },
      ],
      childrenRule: { maxAge: 0, freeStay: true, additionalChildCharge: 0 },
    });
    setIsModalVisible(true);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData(policy);
    setIsModalVisible(true);
  };

  const handleDelete = async (policyId) => {
    try {
      await axios.delete(`/api/policies/delete/${policyId}`);
      message.success("Policy deleted successfully");
      fetchPoliciesAndExtraBedPrice();
    } catch (error) {
      message.error("Failed to delete policy");
    }
  };

  const handleExtraBedPriceSubmit = async () => {
    try {
      await axios.post("/api/extrabedprice/set", { price: extraBedPrice });
      Swal.fire("Success!", "Extra bed price has been updated.", "success");
    } catch (error) {
      Swal.fire("Error!", "Failed to update extra bed price.", "error");
    }
  };

  const handleSaveChildrenPolicy = async () => {
    try {
      const childrenAgeRule = {
        freeAge: parseInt(formData.childrenAgeRule.freeAge, 10) || 0,
        maxAge: parseInt(formData.childrenAgeRule.maxAge, 10) || 0,
        extraPrice:
          typeof formData.childrenAgeRule.extraPrice !== "undefined"
            ? parseInt(formData.childrenAgeRule.extraPrice, 10) || 0
            : 0,
      };

      // Try to get existing tenant-level children age policy
      try {
        if (childrenPolicy && childrenPolicy._id) {
          await axios.put(`/api/childrenpolicy/update/${childrenPolicy._id}`, {
            childrenAgeRule,
          });
          message.success("Children age policy updated");
          return;
        } else {
          // Create if not exists
          await axios.post("/api/childrenpolicy/create", { childrenAgeRule });
          message.success("Children age policy created");
        }
      } catch (err) {
        if (!(err.response && err.response.status === 404)) {
          // unexpected error
          console.error(err);
          message.error("Failed to fetch existing children age policy");
          return;
        }
        // 404 -> not found, fallthrough to create
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to save children age policy");
    }
  };

  return {
    policies,
    formData,
    editingPolicy,
    isModalVisible,
    extraBedPrice,
    setExtraBedPrice,
    setIsModalVisible,
    handleCancellationRuleChange,
    handleChildrenRuleChange,
    handleModalOk,
    handleCreate,
    handleEdit,
    handleDelete,
    handleExtraBedPriceSubmit,
    handleSaveChildrenPolicy,
    handleChildrenAgeRuleChange,
  };
};
